import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Funnel, MagnifyingGlass, CheckCircle, XCircle, Clock, ArrowsClockwise } from '@phosphor-icons/react';
import { API } from '@/App';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

export default function Logs() {
  const [searchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [filters, setFilters] = useState({ connection_id: '', execution_type: '', status: '' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [logsRes, connsRes] = await Promise.all([fetch(`${API}/logs?limit=200`, { credentials: 'include' }), fetch(`${API}/connections`, { credentials: 'include' })]);
      if (logsRes.ok) setLogs(await logsRes.json());
      if (connsRes.ok) setConnections(await connsRes.json());
    } catch (error) { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  };

  const getStatusColor = (status) => {
    if (status < 300) return 'text-kova-green';
    if (status < 400) return 'text-kova-cyan';
    if (status < 500) return 'text-kova-amber';
    return 'text-kova-red';
  };

  const getMethodColor = (method) => ({ GET: 'text-kova-green', POST: 'text-kova-cyan', PUT: 'text-kova-amber', DELETE: 'text-kova-red', PATCH: 'text-kova-purple' }[method] || 'text-white');
  const formatTime = (dateStr) => new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const getConnectionName = (id) => connections.find(c => c.connection_id === id)?.name || 'Unknown';

  const filteredLogs = logs.filter(log => {
    if (filters.connection_id && log.connection_id !== filters.connection_id) return false;
    if (filters.execution_type && log.execution_type !== filters.execution_type) return false;
    if (filters.status === 'success' && log.response_status >= 400) return false;
    if (filters.status === 'error' && log.response_status < 400) return false;
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><span className="font-mono text-kova-cyan"><span className="ascii-spinner mr-2"></span>LOADING LOGS</span></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading font-bold text-2xl">Execution Logs</h1><p className="text-kova-slate text-sm mt-1">View and analyze API execution history</p></div>
        <button onClick={fetchData} className="p-2 text-kova-silv hover:text-white hover:bg-kova-card2 transition-colors" data-testid="refresh-logs"><ArrowsClockwise size={20} /></button>
      </div>

      <div className="flex flex-wrap gap-3 p-4 bg-kova-card border border-kova-cyan/12">
        <Select value={filters.connection_id} onValueChange={(v) => setFilters(prev => ({ ...prev, connection_id: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-48 bg-kova-bg border-kova-cyan/12 rounded-none"><SelectValue placeholder="All Connections" /></SelectTrigger>
          <SelectContent className="bg-kova-card border-kova-cyan/12"><SelectItem value="all">All Connections</SelectItem>{connections.map(c => <SelectItem key={c.connection_id} value={c.connection_id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.execution_type} onValueChange={(v) => setFilters(prev => ({ ...prev, execution_type: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-40 bg-kova-bg border-kova-cyan/12 rounded-none"><SelectValue placeholder="All Types" /></SelectTrigger>
          <SelectContent className="bg-kova-card border-kova-cyan/12"><SelectItem value="all">All Types</SelectItem><SelectItem value="manual">Manual</SelectItem><SelectItem value="scheduled">Scheduled</SelectItem><SelectItem value="nl_query">NL Query</SelectItem></SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-36 bg-kova-bg border-kova-cyan/12 rounded-none"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent className="bg-kova-card border-kova-cyan/12"><SelectItem value="all">All Status</SelectItem><SelectItem value="success">Success</SelectItem><SelectItem value="error">Error</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="terminal-bg">
        <table className="w-full">
          <thead><tr className="text-kova-slate text-xs font-mono uppercase tracking-wide"><th className="text-left p-3">Status</th><th className="text-left p-3">Method</th><th className="text-left p-3">URL</th><th className="text-left p-3">Type</th><th className="text-left p-3">Time</th><th className="text-left p-3">Duration</th></tr></thead>
          <tbody className="terminal-text">
            {filteredLogs.length > 0 ? filteredLogs.map((log) => (
              <tr key={log.log_id} className="border-t border-[#1a1a1a] hover:bg-kova-bg cursor-pointer" onClick={() => setSelectedLog(log)} data-testid={`log-row-${log.log_id}`}>
                <td className="p-3"><span className={`flex items-center gap-2 ${getStatusColor(log.response_status)}`}>{log.response_status < 400 ? <CheckCircle size={14} weight="fill" /> : <XCircle size={14} weight="fill" />}{log.response_status}</span></td>
                <td className={`p-3 ${getMethodColor(log.request_method)}`}>{log.request_method}</td>
                <td className="p-3 text-kova-silv max-w-xs truncate">{log.request_url}</td>
                <td className="p-3"><span className={`badge ${log.execution_type === 'scheduled' ? 'badge-info' : log.execution_type === 'nl_query' ? 'badge-warning' : 'badge-success'}`}>{log.execution_type}</span></td>
                <td className="p-3 text-kova-slate">{formatTime(log.created_at)}</td>
                <td className="p-3 text-kova-slate">{log.response_time_ms}ms</td>
              </tr>
            )) : <tr><td colSpan="6" className="p-8 text-center text-kova-slate">No logs found</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="bg-kova-card border-kova-cyan/12 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading font-bold text-xl">Log Details</DialogTitle></DialogHeader>
          {selectedLog && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="text-kova-slate text-xs block mb-1">STATUS</span><span className={`font-mono text-lg ${getStatusColor(selectedLog.response_status)}`}>{selectedLog.response_status}</span></div>
                <div><span className="text-kova-slate text-xs block mb-1">DURATION</span><span className="font-mono text-lg">{selectedLog.response_time_ms}ms</span></div>
              </div>
              <div><span className="text-kova-slate text-xs block mb-1">REQUEST</span><div className="p-3 bg-kova-bg border border-kova-cyan/12 font-mono text-sm"><span className={getMethodColor(selectedLog.request_method)}>{selectedLog.request_method}</span> <span className="text-kova-silv">{selectedLog.request_url}</span></div></div>
              {selectedLog.error_message && <div><span className="text-kova-slate text-xs block mb-1">ERROR</span><div className="p-3 bg-kova-red/10 border border-kova-red/30 font-mono text-sm text-kova-red">{selectedLog.error_message}</div></div>}
              <div><span className="text-kova-slate text-xs block mb-1">RESPONSE</span><div className="terminal-bg p-4 max-h-64 overflow-auto"><pre className="terminal-text text-kova-green whitespace-pre-wrap break-all">{typeof selectedLog.response_body === 'string' ? selectedLog.response_body : JSON.stringify(selectedLog.response_body, null, 2)}</pre></div></div>
              <div className="text-kova-slate text-xs font-mono">Log ID: {selectedLog.log_id} | {formatTime(selectedLog.created_at)}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
